import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import FinanceDashboardHome from './pages/FinanceDashboardHome';
import PartnerDetailPage from './pages/PartnerDetailPage';
import Unauthorized from './pages/Unauthorized';

// Subscriber Wallet Pages
import SubscriberPortalHome from './pages/SubscriberPortalHome';
import SubscriberProfile from './pages/SubscriberProfile';
import SubscriberList from './pages/SubscriberList';
import ManualTransaction from './pages/ManualTransaction';
import ManualTransactionProfile from './pages/ManualTransactionProfile';
import ManualHospitalPaymentForm from './pages/ManualHospitalPaymentForm';
import HospitalPaymentList from './pages/HospitalPaymentList';
import HospitalBookingProfile from './pages/HospitalBookingProfile';
import RedemptionRequestsList from './pages/RedemptionRequestsList';
import RedemptionSubscriberDetail from './pages/RedemptionSubscriberDetail';
import DiagnosticSubscribersList from './pages/DiagnosticSubscribersList';
import DiagnosticBookingProfile from './pages/DiagnosticBookingProfile';
import MedicineSubscribersList from './pages/MedicineSubscribersList';
import MedicineBookingProfile from './pages/MedicineBookingProfile';
import MedicineTransaction from './pages/MedicineTransaction';
import DoctorList from './pages/DoctorList';
import DoctorSlotManagement from './pages/DoctorSlotManagement';
import VirtualConsultationList from './pages/VirtualConsultationList';
import VirtualConsultationBooking from './pages/VirtualConsultationBooking';
import EngagementRuleConfig from './pages/EngagementRuleConfig';
import EngagementMessageLogs from './pages/EngagementMessageLogs';
import ApplicationEvents from './pages/ApplicationEvents';
import AccountDeletionRequestsList from './pages/AccountDeletionRequestsList';
import HospitalList from './pages/HospitalList';
import AddHospital from './pages/AddHospital';
import HospitalDetail from './pages/HospitalDetail';
import DiagnosticLabsManagement from './pages/DiagnosticLabsManagement';
import DiagnosticChainDetail from './pages/DiagnosticChainDetail';
import DiagnosticLabsList from './pages/DiagnosticLabsList';
import DiagnosticLabWizard from './pages/DiagnosticLabWizard';
import DiagnosticLabDetail from './pages/DiagnosticLabDetail';
import DiagnosticChainTestLists from './pages/DiagnosticChainTestLists';
import PartnerEnquiryList from './pages/PartnerEnquiryList';

import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './auth/AuthContext';
import FinanceLayout from './components/FinanceLayout'; // path depends on your project structure
import SubscriberWalletLayout from './components/SubscriberWalletLayout';
import SubAnchorDetailPage from 'pages/SubAnchorDetailPage';
import AddMedicine from './pages/AddMedicine';
import AddMedicineForm from './pages/AddMedicineForm';
import DiagnosticTransaction from './pages/DiagnosticTransaction';
import LoanSubscribersList from './pages/LoanSubscribersList';
import LoanSubscriberDetail from './pages/LoanSubscriberDetail';
import CreditLineSubscribersList from './pages/CreditLineSubscribersList';
import CreditLineSubscriberDetail from './pages/CreditLineSubscriberDetail';
import AdminUsersList from './pages/AdminUsersList';
import BulkIssueSubscriptions from './pages/BulkIssueSubscriptions';


function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Finance Layout for finance_user */}
          <Route
            element={
              <ProtectedRoute allowedRoles={['finance_user', 'tp_admin', 'wallet_superadmin', 'wallet_finance']}>
                <FinanceLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/financedashboard" element={<FinanceDashboardHome />} />
            <Route path="/partner-detail/:id" element={<PartnerDetailPage />} />
            <Route path="/partner-detail/:id/:dsa_code" element={<SubAnchorDetailPage />} />
          </Route>

          {/* Subscriber Wallet Routes - Separate from Finance Layout */}
          <Route path="/subscriber-wallet/*" element={
            <ProtectedRoute allowedRoles={['wallet_superadmin', 'wallet_finance', 'wallet_ops', 'wallet_support']}>
              <SubscriberWalletLayout />
            </ProtectedRoute>
          }>
            {/* Default landing page */}
            <Route index element={<SubscriberPortalHome />} />
            <Route path="home" element={<SubscriberPortalHome />} />

            {/* Subscriber list page */}
            <Route path="subscribers" element={<SubscriberList />} />

            {/* Diagnostics page */}
            <Route path="diagnostics" element={<DiagnosticSubscribersList />} />
            <Route path="diagnostic-booking/:id" element={<DiagnosticBookingProfile />} />
            {/* Medicine page */}
            <Route path="medicine" element={<MedicineSubscribersList />} />
            <Route path="medicine-booking/:id" element={<MedicineBookingProfile />} />
            <Route path="add-medicine" element={<AddMedicine />} />
            <Route path="add-medicine/form" element={<AddMedicineForm />} />

            {/* Doctor page */}
            <Route path="doctors" element={<DoctorList />} />
            <Route path="doctors/:doctorId/slots" element={<DoctorSlotManagement />} />

            {/* Virtual Consultation page */}
            <Route path="virtual-consultation" element={<VirtualConsultationList />} />

            {/* Customer Engagement Platform (SuperAdmin only) */}
            <Route path="engagement" element={<EngagementRuleConfig />} />
            <Route path="engagement-logs" element={<EngagementMessageLogs />} />
            <Route path="application-events" element={<ApplicationEvents />} />
            {/* Hospital CMS pages */}
            <Route path="hospitals" element={<HospitalList />} />
            <Route path="hospitals/add" element={<AddHospital />} />
            <Route path="hospitals/:hospitalId" element={<HospitalDetail />} />

            {/* AyushPay Labs — Diagnostic Management */}
            <Route path="labs" element={<DiagnosticLabsManagement />} />
            <Route path="labs/chains/:chainId" element={<DiagnosticChainDetail />} />
            <Route path="labs/chains/:chainId/test-lists" element={<DiagnosticChainTestLists />} />
            <Route path="labs/labs" element={<DiagnosticLabsList />} />
            <Route path="labs/labs/new" element={<DiagnosticLabWizard />} />
            <Route path="labs/labs/:labId" element={<DiagnosticLabDetail />} />

            {/* Manual Transaction page */}
            <Route path="manual-transaction" element={<ManualTransaction />} />
            <Route path="manual-transaction/subscriber/:id" element={<ManualTransactionProfile />} />
            <Route path="hospital-payment" element={<HospitalPaymentList />} />
            <Route path="hospital-booking/:id" element={<HospitalBookingProfile />} />
            <Route path="subscriber/:id/hospital-payment" element={<ManualHospitalPaymentForm />} />

            {/* Redemption Requests page */}
            <Route path="redemption-requests" element={<RedemptionRequestsList />} />
            <Route path="redemption-requests/subscriber/:id" element={<RedemptionSubscriberDetail />} />

            {/* Account Deletion Requests page */}
            <Route path="account-deletion-requests" element={<AccountDeletionRequestsList />} />

            {/* Admin User Management (Super Admin only — page self-gates) */}
            <Route path="admin-users" element={<AdminUsersList />} />

            {/* Bulk subscription issuance. The enclosing route admits four roles,
                so this one re-gates to superadmin — confirming a file issues
                subscriptions and messages every subscriber in it. */}
            <Route
              path="bulk-issue-subscriptions"
              element={
                <ProtectedRoute allowedRoles={['wallet_superadmin']}>
                  <BulkIssueSubscriptions />
                </ProtectedRoute>
              }
            />

            {/* Ayushpay Leads (Partner Enquiries) page */}
            <Route path="partner-enquiries" element={<PartnerEnquiryList />} />

            {/* Loan Subscribers */}
            <Route path="loan-subscribers" element={<LoanSubscribersList />} />
            <Route path="loan-subscribers/:application_id/detail" element={<LoanSubscriberDetail />} />

            {/* Credit Line Subscribers. The enclosing route admits four roles,
                so this one re-gates to ops + superadmin only, matching the
                backend API's permission scope. */}
            <Route
              path="credit-line-subscribers"
              element={
                <ProtectedRoute allowedRoles={['wallet_ops', 'wallet_superadmin']}>
                  <CreditLineSubscribersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="credit-line-subscribers/:application_id/detail"
              element={
                <ProtectedRoute allowedRoles={['wallet_ops', 'wallet_superadmin']}>
                  <CreditLineSubscriberDetail />
                </ProtectedRoute>
              }
            />

            {/* Detail profile route from list row click */}
            <Route path="subscriber/:id" element={<SubscriberProfile />} />
            <Route path="subscriber/:id/medicine-transaction" element={<MedicineTransaction />} />
            <Route path="subscriber/:id/diagnostic-transaction" element={<DiagnosticTransaction />} />
            <Route path="subscriber/:id/virtual-consultation" element={<VirtualConsultationBooking />} />
          </Route>

          {/* Internal user routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['internal_user']}>
                <Navigate to="/dashboard/subscription" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/:section"
            element={
              <ProtectedRoute allowedRoles={['internal_user']}>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Login />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
